import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import {
  auctionRequiresListingApproval,
  parseAuctionPublishResponse,
} from "../../utils/auctionListingApproval";
import "./MyAuctions.scss";

export default function MyAuctions() {
  const navigate = useNavigate();
  const [allAuctions, setAllAuctions] = useState([]); // All auctions for the logged-in user
  const [auctions, setAuctions] = useState([]); // Filtered auctions for display
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [reopenPromptAuctionId, setReopenPromptAuctionId] = useState(null); // auction_id showing Reopen Bid flow
  const [reopenStep, setReopenStep] = useState(null); // 'extend' | 'close' | null
  const [extendDays, setExtendDays] = useState(7);
  const [extendReservePrice, setExtendReservePrice] = useState("");
  const [extending, setExtending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [inspectionRFQStatusByAuction, setInspectionRFQStatusByAuction] = useState({}); // auctionId -> RFQ status (for QA flow)
  const [inspectionDecisionByAuction, setInspectionDecisionByAuction] = useState({}); // auctionId -> 'accept' | 'reject' | null

  useEffect(() => {
    fetchAuctions();
  }, []);

  useEffect(() => {
    // Filter auctions based on status filter
    if (filterStatus !== "all") {
      const filtered = allAuctions.filter((a) => a.status === filterStatus);
      setAuctions(filtered);
    } else {
      setAuctions(allAuctions);
    }
  }, [filterStatus, allAuctions]);

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get(API_URLS.AUCTION.MY_AUCTIONS);

      if (response && response.success && response.data) {
        const fetchedAuctions = Array.isArray(response.data) ? response.data : [];
        setAllAuctions(fetchedAuctions);
        
        // Apply initial filter
        if (filterStatus !== "all") {
          const filtered = fetchedAuctions.filter((a) => a.status === filterStatus);
          setAuctions(filtered);
        } else {
          setAuctions(fetchedAuctions);
        }
        // For QA-flow auctions with payment completed, fetch inspection RFQ status and buyer decision
        fetchInspectionStatusForQAFlow(fetchedAuctions);
      } else {
        setAllAuctions([]);
        setAuctions([]);
      }
    } catch (error) {
      console.error("Error fetching auctions:", error);
      toast.error("Failed to fetch auctions");
      setAllAuctions([]);
      setAuctions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectionStatusForQAFlow = async (auctionsList) => {
    const qaFlowAuctions = (auctionsList || []).filter(
      (a) =>
        a.seller_decision === "accepted" &&
        a.winning_bid_id &&
        a.payment_status === "completed" &&
        (a.inspection_allowed === "Yes" || a.inspectionAllowed === "Yes") &&
        (a.inspector_type === "Private Inspector" || a.inspectorType === "Private Inspector")
    );
    const rfqStatusMap = {};
    const decisionMap = {};
    for (const auction of qaFlowAuctions) {
      const aid = auction.auction_id;
      try {
        const rfqRes = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_RFQ_BY_AUCTION(aid));
        if (rfqRes && rfqRes.success && rfqRes.data) {
          rfqStatusMap[aid] = rfqRes.data.status || null;
        } else {
          rfqStatusMap[aid] = null;
        }
      } catch (e) {
        if (e.response?.status !== 404) console.error(`Error fetching RFQ for auction ${aid}:`, e);
        rfqStatusMap[aid] = rfqStatusMap[aid] ?? null;
      }
      try {
        const decRes = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_DECISION_BY_AUCTION(aid));
        if (decRes && decRes.success && decRes.data) {
          decisionMap[aid] = decRes.data.decision || null;
        } else {
          decisionMap[aid] = null;
        }
      } catch (e) {
        if (e.response?.status !== 404) console.error(`Error fetching inspection decision for auction ${aid}:`, e);
        decisionMap[aid] = decisionMap[aid] ?? null;
      }
    }
    setInspectionRFQStatusByAuction(rfqStatusMap);
    setInspectionDecisionByAuction(decisionMap);
  };

  const hasQAFlow = (auction) =>
    auction &&
    (auction.inspection_allowed === "Yes" || auction.inspectionAllowed === "Yes") &&
    (auction.inspector_type === "Private Inspector" || auction.inspectorType === "Private Inspector");

  const showProductCollectionStatus = (auction) => {
    if (!auction || auction.seller_decision !== "accepted" || !auction.winning_bid_id || auction.payment_status !== "completed") {
      return false;
    }
    if (!hasQAFlow(auction)) {
      return true;
    }
    const rfqStatus = inspectionRFQStatusByAuction[auction.auction_id];
    const decision = inspectionDecisionByAuction[auction.auction_id];
    if (rfqStatus == null || rfqStatus === "") {
      return true;
    }
    return rfqStatus === "completed" && (decision === "accept" || decision === "accepted");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status, isActive = false, auction = null) => {
    if (auction?.status === "ended" && auction?.winning_bid_id) {
      return (
        <span className="status-badge status-badge-success">
          Winner Declared
        </span>
      );
    }
    if (auction?.status === "ended" && auction?.seller_decision === "expired") {
      return (
        <span className="status-badge status-badge-warning">
          Decision Expired
        </span>
      );
    }

    const statusConfig = {
      draft: { label: "Draft", variant: "draft" },
      pending_approval: { label: "Pending approval", variant: "warning" },
      approved: { label: "Approved", variant: "success" },
      rejected: { label: "Rejected", variant: "danger" },
      published: { label: "Published", variant: "info" },
      active: { label: "Active", variant: "success" },
      paused: { label: "Paused", variant: "warning" },
      ended: { label: "Ended", variant: "danger" },
      cancelled: { label: "Cancelled", variant: "draft" },
      extended: { label: "Extended", variant: "info" },
    };

    const config = statusConfig[status] || { label: status, variant: "draft" };
    return (
      <span
        className={`status-badge status-badge-${config.variant} ${isActive ? 'active-badge' : ''}`}
      >
        {config.label}
      </span>
    );
  };

  const handlePublish = async (auctionId) => {
    try {
      const response = await CommonService.post(
        API_URLS.AUCTION.PUBLISH(auctionId)
      );
      if (response && response.success) {
        const data = parseAuctionPublishResponse(response);
        if (auctionRequiresListingApproval(data) || data?.published === false) {
          toast.info(
            data.message ||
              "Auction submitted for approval. It will appear on the public listing after approval.",
          );
        } else {
          toast.success(data?.message || "Auction published successfully!");
        }
        fetchAuctions();
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to publish auction";
      toast.error(errorMessage);
    }
  };

  const handleEndAuction = async (auctionId) => {
    if (!window.confirm("Are you sure you want to end this auction?")) {
      return;
    }

    try {
      const response = await CommonService.post(API_URLS.AUCTION.END(auctionId));
      if (response && response.success) {
        toast.success("Auction ended successfully!");
        fetchAuctions();
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to end auction";
      toast.error(errorMessage);
    }
  };

  // Payment deadline passed: seller_decision accepted, winning_bid_id, payment not completed, and (sla_deadline passed or 72h from invoice passed)
  const isPaymentDeadlinePassed = (auction) => {
    if (!auction || auction.seller_decision !== "accepted" || !auction.winning_bid_id) return false;
    if (auction.payment_status === "completed") return false;
    const now = Date.now();
    if (auction.payment_sla_deadline) {
      return new Date(auction.payment_sla_deadline).getTime() <= now;
    }
    if (auction.invoice_created_at) {
      const deadline = new Date(auction.invoice_created_at).getTime() + 72 * 60 * 60 * 1000;
      return deadline <= now;
    }
    return false;
  };

  const handleReopenYes = (auction) => {
    setReopenPromptAuctionId(auction.auction_id);
    setReopenStep("extend");
    setExtendDays(7);
    setExtendReservePrice("");
  };

  const handleReopenNo = (auction) => {
    setReopenPromptAuctionId(auction.auction_id);
    setReopenStep("close");
  };

  const handleExtendConfirm = async () => {
    if (!reopenPromptAuctionId) return;
    const days = parseInt(extendDays, 10);
    if (isNaN(days) || days < 1) {
      toast.error("Please enter a valid number of days (at least 1)");
      return;
    }
    try {
      setExtending(true);
      const body = { extension_days: days };
      if (extendReservePrice && extendReservePrice.trim() !== "") {
        const price = parseFloat(extendReservePrice);
        if (!isNaN(price) && price > 0) body.new_reserve_price = price;
      }
      const response = await CommonService.post(
        `${API_URLS.AUCTION.BASE}/${reopenPromptAuctionId}/extend`,
        body
      );
      if (response && response.success) {
        toast.success("Auction extended successfully!");
        setReopenPromptAuctionId(null);
        setReopenStep(null);
        fetchAuctions();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.response?.data?.error || "Failed to extend auction";
      toast.error(msg);
    } finally {
      setExtending(false);
    }
  };

  const handleCloseConfirm = async () => {
    if (!reopenPromptAuctionId) return;
    try {
      setClosing(true);
      const response = await CommonService.post(API_URLS.AUCTION.REOPEN_BID(reopenPromptAuctionId), {});
      if (response && response.success) {
        toast.success("Bid closed. You can declare a new winner from Review Bids.");
        setReopenPromptAuctionId(null);
        setReopenStep(null);
        fetchAuctions();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.response?.data?.error || "Failed to close bid";
      toast.error(msg);
    } finally {
      setClosing(false);
    }
  };

  const closeReopenModals = () => {
    setReopenPromptAuctionId(null);
    setReopenStep(null);
  };

  return (
    <div className="my-auctions-page">
      <div className="container">
        <div className="page-header">
          <h1>My Auctions</h1>
          <button
            className="btn-primary"
            onClick={() => navigate("/auction/create")}
          >
            + Create New Auction
          </button>
        </div>

        {/* Statistics - Calculated from all auctions for the logged-in user */}
        <div className="statistics">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-value">{allAuctions.length}</div>
            <div className="stat-label">Total Auctions</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">❌</div>
            <div className="stat-value">
              {allAuctions.reduce((sum, a) => sum + (a.rejected_bids || 0), 0)}
            </div>
            <div className="stat-label">Total Rejected Bids</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-value">
              {allAuctions.filter((a) => a.status === "ended" && a.winning_bid_id).length}
            </div>
            <div className="stat-label">Sold</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-value">
              {allAuctions.reduce((sum, a) => sum + (a.total_bids || 0), 0)}
            </div>
            <div className="stat-label">Total Active Bids</div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters">
          <button
            className={filterStatus === "all" ? "active" : ""}
            onClick={() => setFilterStatus("all")}
          >
            All
          </button>
          <button
            className={filterStatus === "draft" ? "active" : ""}
            onClick={() => setFilterStatus("draft")}
          >
            Draft
          </button>
          <button
            className={filterStatus === "active" ? "active" : ""}
            onClick={() => setFilterStatus("active")}
          >
            Active
          </button>
          <button
            className={filterStatus === "ended" ? "active" : ""}
            onClick={() => setFilterStatus("ended")}
          >
            Ended
          </button>
        </div>

        {/* Auctions List */}
        {loading ? (
          <div className="loading">Loading auctions...</div>
        ) : auctions.length === 0 ? (
          <div className="no-auctions">
            <p>No auctions found</p>
            <button
              className="btn-primary"
              onClick={() => navigate("/auction/create")}
            >
              Create Your First Auction
            </button>
          </div>
        ) : (
          <div className="auctions-list">
            {auctions.map((auction) => (
              <div key={auction.auction_id} className="auction-item">
                <div className="auction-info">
                  <div className="auction-header">
                    <div className="auction-title-wrapper">
                      <h3
                        className="auction-product-name-link"
                        onClick={() => navigate(`/auction/${auction.auction_id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/auction/${auction.auction_id}`);
                          }
                        }}
                      >
                        {auction.product_name}
                      </h3>
                      {(auction.status === "active" || auction.status === "ended") && getStatusBadge(auction.status, auction.status === "active", auction)}
                    </div>
                    {auction.status !== "active" && auction.status !== "ended" && getStatusBadge(auction.status, false, auction)}
                  </div>
                  <p className="description">
                    {auction.product_description?.substring(0, 150)}...
                  </p>
                  <div className="auction-details">
                    <div className="detail">
                      <span className="label">Reserve Price:</span>
                      <span className="value">
                        {formatCurrency(auction.reserve_price || 0)}
                      </span>
                    </div>
                    <div className="detail">
                      <span className="label">Highest Bid:</span>
                      <span className="value highlight">
                        {formatCurrency(auction.highest_bid_amount || 0)}
                      </span>
                    </div>
                    {auction.seller_decision === 'accepted' && auction.winning_bid_id ? (
                      <div className="detail">
                        <span className="label">Accepted Bid:</span>
                        <span 
                          className="value clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/auction/${auction.auction_id}/review-bids`);
                          }}
                          style={{ 
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: '#16A34A',
                            fontWeight: '600'
                          }}
                        >
                          1
                        </span>
                      </div>
                    ) : (
                      <div className="detail">
                        <span className="label">Active Bids:</span>
                        <span 
                          className={`value ${auction.total_bids > 0 ? 'clickable' : ''}`}
                          onClick={(e) => {
                            if (auction.total_bids > 0) {
                              e.stopPropagation();
                              navigate(`/auction/${auction.auction_id}/review-bids`);
                            }
                          }}
                          style={{ 
                            cursor: auction.total_bids > 0 ? 'pointer' : 'default',
                            textDecoration: auction.total_bids > 0 ? 'underline' : 'none',
                            color: auction.total_bids > 0 ? '#ff6b35' : 'inherit'
                          }}
                        >
                          {auction.total_bids || 0}
                        </span>
                      </div>
                    )}
                    <div className="detail">
                      <span className="label">Rejected Bids:</span>
                      <span 
                        className={`value ${auction.rejected_bids > 0 ? 'clickable' : ''}`}
                        onClick={(e) => {
                          if (auction.rejected_bids > 0) {
                            e.stopPropagation();
                            navigate(`/auction/${auction.auction_id}/review-bids?filter=rejected`);
                          }
                        }}
                        style={{ 
                          cursor: auction.rejected_bids > 0 ? 'pointer' : 'default',
                          textDecoration: auction.rejected_bids > 0 ? 'underline' : 'none',
                          color: auction.rejected_bids > 0 ? '#dc3545' : '#dc3545'
                        }}
                      >
                        {auction.rejected_bids || 0}
                      </span>
                    </div>
                    <div className="detail">
                      <span className="label">End Date:</span>
                      <span className="value">
                        {new Date(auction.end_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                {isPaymentDeadlinePassed(auction) && (
                  <div className="reopen-bid-prompt">
                    <p className="reopen-bid-message">
                      Buyer did not complete payment within 72 hours (excluding holidays). Reopen Bid?
                    </p>
                    <div className="reopen-bid-buttons">
                      <button
                        type="button"
                        className="btn-reopen-yes"
                        onClick={(e) => { e.stopPropagation(); handleReopenYes(auction); }}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className="btn-reopen-no"
                        onClick={(e) => { e.stopPropagation(); handleReopenNo(auction); }}
                      >
                        No
                      </button>
                    </div>
                  </div>
                )}
                <div className="auction-actions">
                  {auction.status === "draft" && (
                    <button
                      className="btn-publish"
                      onClick={() => handlePublish(auction.auction_id)}
                    >
                      Publish
                    </button>
                  )}
                  {auction.status === "pending_approval" ? (
                    <span className="small text-muted">
                      Awaiting approval — visible on listing after all levels approve
                    </span>
                  ) : null}
                  {auction.status === "rejected" ? (
                    <span className="small text-danger">Listing approval was rejected</span>
                  ) : null}
                  {auction.status === "active" && (
                    <button
                      className="btn-end"
                      onClick={() => handleEndAuction(auction.auction_id)}
                    >
                      End Auction
                    </button>
                  )}
                  {auction.status === "ended" && auction.winning_bid_id && (
                    <button
                      className="btn-review"
                      onClick={() =>
                        navigate(`/auction/${auction.auction_id}/review-bids`)
                      }
                    >
                      Review Bids
                    </button>
                  )}
                  {auction.seller_decision === "accepted" && auction.winning_bid_id && (
                    <>
                      <button
                        className="btn-view-buyer"
                        onClick={() =>
                          navigate(`/auction/${auction.auction_id}/party-connection`)
                        }
                      >
                        View Buyer Details
                      </button>
                      <button
                        className="btn-invoice"
                        onClick={() =>
                          navigate(`/auction/${auction.auction_id}/proforma-invoice`)
                        }
                      >
                        Invoice
                      </button>
                      {showProductCollectionStatus(auction) && (
                        <button
                          className="btn-collection"
                          onClick={() =>
                            navigate(`/auction/${auction.auction_id}/collection-delivery`)
                          }
                        >
                          {auction.seller_confirmation === "confirmed" 
                            ? "View Product Collection Status" 
                            : "Product Collection Status"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Extend Auction Modal (Reopen Bid - Yes) */}
        {reopenStep === "extend" && reopenPromptAuctionId && (
          <div className="reopen-modal-overlay" onClick={closeReopenModals}>
            <div className="reopen-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Extend Auction Date</h3>
              <p className="reopen-modal-desc">Give the buyer more time by extending the auction end date using the existing extension flow.</p>
              <div className="reopen-form-group">
                <label htmlFor="extend-days">Extension (days)</label>
                <input
                  id="extend-days"
                  type="number"
                  min="1"
                  value={extendDays}
                  onChange={(e) => setExtendDays(e.target.value)}
                  className="reopen-input"
                />
              </div>
              <div className="reopen-form-group">
                <label htmlFor="extend-reserve">New reserve price (optional)</label>
                <input
                  id="extend-reserve"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Leave empty to keep current"
                  value={extendReservePrice}
                  onChange={(e) => setExtendReservePrice(e.target.value)}
                  className="reopen-input"
                />
              </div>
              <div className="reopen-modal-actions">
                <button type="button" className="btn-cancel" onClick={closeReopenModals}>Cancel</button>
                <button type="button" className="btn-confirm" onClick={handleExtendConfirm} disabled={extending}>
                  {extending ? "Extending…" : "Extend auction"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close Bid Confirm Modal (Reopen Bid - No) */}
        {reopenStep === "close" && reopenPromptAuctionId && (
          <div className="reopen-modal-overlay" onClick={closeReopenModals}>
            <div className="reopen-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Close the bid?</h3>
              <p className="reopen-modal-desc">
                Do you want to close the bid? This will release the winning bid. You can then declare a new winner from Review Bids.
              </p>
              <div className="reopen-modal-actions">
                <button type="button" className="btn-cancel" onClick={closeReopenModals}>No</button>
                <button type="button" className="btn-confirm btn-close-bid" onClick={handleCloseConfirm} disabled={closing}>
                  {closing ? "Closing…" : "Yes, close bid"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
